import React, { useEffect, useState } from "react";
import { Carousel } from "react-bootstrap";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { motion } from "framer-motion";
import axios from '../lib/axios'
import YoutubeModal from './YoutubeModal';
import { getYoutubeVideoId } from '../utils/youtube';
import { SiYoutubemusic } from "react-icons/si";

const MyCarousel = () => {
  const backendURL = process.env.REACT_APP_BACKEND_URL;
  const [banners, setBanners] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isYoutubeModalOpen, setIsYoutubeModalOpen] = useState(false);
  const [currentVideoId, setCurrentVideoId] = useState(null);
  const [isMobileViewport, setIsMobileViewport] = useState(false);

  const pathname = window.location.pathname;
  const page = pathname.split("/")[1];

  useEffect(() => {
    const getBanners = async () => {
      try {
        const res = await axios.get(`${backendURL}/api/globals/banners`);
        if (res.status !== 200) throw new Error (res.statusText);
        // const banners = res.data[page]
        const banners = await res.data;
        const sortBanners = (list = []) =>
          [...list].sort(
            (a, b) => Number(a?.sort_order || 0) - Number(b?.sort_order || 0),
          );

        if (page === "designing") {
          setBanners(sortBanners(banners?.images ?? []));
          return;
        } else if (page === "printing") {
          setBanners(sortBanners(banners?.printing ?? []));
          return;
        } else if (page === "advertising") {
          setBanners(sortBanners(banners?.advertising ?? []));
          return;
        } else if (page === "influencer-marketing") {
          setBanners(sortBanners(banners?.influencerMarketing ?? []));
          return;
        } else if (page === "brand-endorsement") {
          setBanners(sortBanners(banners?.brandEndorsement ?? []));
          return;
        } else if (page === "digital-marketing") {
          setBanners(sortBanners(banners?.digitalMarketing ?? []));
          return;
        }
        setBanners(sortBanners(banners?.home ?? []));
      } catch (error) {
        console.error('Error fetching banners:', error);
      }
    };

    getBanners();

  }, [page, backendURL]);

  useEffect(() => {
    const updateViewportState = () => {
      setIsMobileViewport(window.innerWidth < 768);
    };

    updateViewportState();
    window.addEventListener("resize", updateViewportState);

    return () => window.removeEventListener("resize", updateViewportState);
  }, []);

  const CustomPrevIcon = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: isHovered || isMobileViewport ? 1 : 0, x: 0 }}
      transition={{ duration: 0.3 }}
      className="absolute left-3 top-1/2 -translate-y-1/2 z-10 sm:left-4"
    >
      <motion.button
        whileHover={{ scale: 1.1 }}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-[#bc1535] shadow-lg backdrop-blur-sm transition-all hover:bg-[#bc1535] hover:text-white sm:h-12 sm:w-12"
      >
        <FaChevronLeft className="text-sm sm:text-xl" />
      </motion.button>
    </motion.div>
  );

  const CustomNextIcon = () => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: isHovered || isMobileViewport ? 1 : 0, x: 0 }}
      transition={{ duration: 0.3 }}
      className="absolute right-3 top-1/2 -translate-y-1/2 z-10 sm:right-4"
    >
      <motion.button
        whileHover={{ scale: 1.1 }}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-[#bc1535] shadow-lg backdrop-blur-sm transition-all hover:bg-[#bc1535] hover:text-white sm:h-12 sm:w-12"
      >
        <FaChevronRight className="text-sm sm:text-xl" />
      </motion.button>
    </motion.div>
  );

  if (!banners.length) return null;

  const bannerImageClass =
    "h-full w-full object-cover object-center sm:object-contain";

  return (
    <motion.div
      className="relative w-full overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Carousel
        activeIndex={activeIndex}
        onSelect={(index) => setActiveIndex(index)}
        prevIcon={<CustomPrevIcon />}
        nextIcon={<CustomNextIcon />}
        indicators={false}
        interval={5000}
        style={{ width: "100%" }}
        fade
        className="carousel-fade storefront-banner-carousel"
      >
        {banners.map((banner, index) => (
          <Carousel.Item key={index}>
            <motion.div
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
              className="relative w-full h-[36vh] min-h-[200px] max-h-[320px] sm:h-auto sm:min-h-0 sm:max-h-[50vh]"
            >
              <img
                src={banner.image?.cloudinary ? banner.image.cloudinary.secure_url : banner.image.url}
                alt={`Banner ${index + 1}`}
                className={bannerImageClass}
                style={{ marginBottom: 0 }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/10 pointer-events-none" />
              {banner.link && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.1 }}
                  className="absolute bottom-3 right-3 z-20 sm:bottom-6 sm:right-6"
                  onClick={(e) => {
                    e.stopPropagation();
                    const videoId = getYoutubeVideoId(banner.link);
                    if (videoId) {
                      setCurrentVideoId(videoId);
                      setIsYoutubeModalOpen(true);
                    } else {
                      window.open(banner.link, '_blank');
                    }
                  }}
                >
                  <button className="rounded-full bg-white p-1.5 shadow-lg sm:p-2">
                    <SiYoutubemusic className="text-2xl text-[#d41a3f] sm:text-4xl" />
                  </button>
                </motion.div>
              )}
            </motion.div>
          </Carousel.Item>
        ))}
      </Carousel>

      {/* Custom Indicators */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 0.5 }}
        className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 gap-2 sm:bottom-4"
      >
        {banners.map((_, index) => (
          <motion.button
            key={index}
            whileHover={{ scale: 1.2 }}
            onClick={() => setActiveIndex(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === activeIndex
                ? "w-6 bg-white sm:w-8"
                : "w-2 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </motion.div>

      {/* Add a subtle shadow overlay on hover */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isHovered ? 0.1 : 0 }}
        transition={{ duration: 0.3 }}
        className="absolute inset-0 bg-black pointer-events-none"
      />

      <YoutubeModal
        isOpen={isYoutubeModalOpen}
        onClose={() => {
          setIsYoutubeModalOpen(false);
          setCurrentVideoId(null);
        }}
        videoId={currentVideoId}
      />
    </motion.div>
  );
};

export default MyCarousel;
